import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import api from '../utils/api';
import API from '../utils/apiconfig';
import { useAuth } from '../context/Authcontext';
import { isValidMobileOptional, parseApiErrors } from '../utils/formErrors';
import { usePasswordVisibility, PasswordVisibilityToggle, PASSWORD_INPUT_CLASS } from '../components/PasswordVisibilityToggle';

const SERIF = { fontFamily: "'DM Serif Display', Georgia, serif" };

// ── Icons ────────────────────────────────────────────────────

const IconUser = ({ className = 'w-4 h-4' }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
  </svg>
);
const IconMail = ({ className = 'w-4 h-4' }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
  </svg>
);
const IconLock = ({ className = 'w-4 h-4' }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
  </svg>
);
const IconPhone = ({ className = 'w-4 h-4' }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
  </svg>
);
const IconGift = ({ className = 'w-4 h-4' }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7" />
  </svg>
);
const IconSpark = ({ className = 'w-5 h-5' }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
  </svg>
);
const IconUsers = ({ className = 'w-5 h-5' }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
  </svg>
);
const IconChart = ({ className = 'w-5 h-5' }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
  </svg>
);

// ── Shared tiny components ───────────────────────────────────

const Field = ({
  placeholder,
  label,
  required = false,
  error,
  hint,
  type = 'text',
  value,
  onChange,
  onEnter,
  autoComplete,
  icon: Icon,
}) => {
  const isPassword = type === 'password';
  const { visible, toggle, inputType } = usePasswordVisibility();
  const hasIcon = Boolean(Icon);
  const inputPad = hasIcon
    ? isPassword ? 'pl-10 pr-10' : 'pl-10 pr-3.5'
    : isPassword ? 'pl-3.5 pr-10' : 'px-3.5';

  return (
    <div>
      {label ? (
        <label className="mb-1.5 block text-[11px] font-bold uppercase tracking-wide text-slate-500">
          {label}
          {required ? <span className="ml-0.5 text-rose-500" aria-hidden>*</span> : null}
          {!required && hint ? (
            <span className="ml-1 font-normal normal-case tracking-normal text-slate-400">({hint})</span>
          ) : null}
        </label>
      ) : null}
      <div className={isPassword || hasIcon ? 'relative' : undefined}>
        {Icon ? (
          <span className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400">
            <Icon />
          </span>
        ) : null}
        <input
          className={`w-full rounded-xl border bg-white/90 py-2.5 text-sm text-slate-800 outline-none transition placeholder-slate-300 shadow-sm ${inputPad} ${
            isPassword ? PASSWORD_INPUT_CLASS : ''
          } ${
            error
              ? 'border-rose-300 focus:border-rose-400 focus:ring-2 focus:ring-rose-200/80'
              : 'border-slate-200/80 focus:border-violet-400 focus:ring-2 focus:ring-violet-200/60'
          }`}
          type={isPassword ? inputType : type}
          placeholder={placeholder}
          value={value}
          onChange={onChange}
          onKeyDown={(e) => e.key === 'Enter' && onEnter?.()}
          autoComplete={autoComplete ?? 'off'}
          aria-invalid={error ? 'true' : undefined}
          required={required}
        />
        {isPassword ? <PasswordVisibilityToggle visible={visible} onToggle={toggle} /> : null}
      </div>
      {error ? (
        <p className="mt-1 text-xs font-medium text-rose-600" role="alert">
          {error}
        </p>
      ) : null}
      {label && required && hint && !error ? (
        <p className="mt-1 text-[11px] text-slate-400">{hint}</p>
      ) : null}
    </div>
  );
};

const ErrBox = ({ msg }) => msg ? (
  <div className="flex items-start gap-2.5 rounded-xl border border-rose-200/80 bg-gradient-to-r from-rose-50 to-red-50 p-3 text-xs text-rose-700">
    <svg className="mt-0.5 h-4 w-4 shrink-0 text-rose-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
    </svg>
    <span>{msg}</span>
  </div>
) : null;

const BlockedAccountDialog = ({ onClose }) => (
  <div
    className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-gradient-to-br from-slate-950/80 via-rose-950/50 to-slate-950/80 backdrop-blur-md"
    role="dialog"
    aria-modal="true"
    aria-labelledby="blocked-account-title"
  >
    <div className="w-full max-w-md overflow-hidden rounded-3xl border border-white/10 shadow-2xl shadow-rose-950/40 transform transition-all">
      {/* Gradient header */}
      <div className="relative overflow-hidden bg-gradient-to-br from-rose-600 via-red-600 to-orange-500 px-6 pt-8 pb-12 text-center">
        <div className="pointer-events-none absolute -right-8 -top-8 h-32 w-32 rounded-full bg-white/10 blur-2xl" />
        <div className="pointer-events-none absolute -bottom-6 -left-6 h-24 w-24 rounded-full bg-orange-300/20 blur-xl" />

        <div className="relative mx-auto mb-4 flex h-[4.25rem] w-[4.25rem] items-center justify-center rounded-2xl bg-white/15 shadow-lg ring-2 ring-white/25 backdrop-blur-sm">
          <svg className="h-8 w-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.75}
              d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
            />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4l16 16" />
          </svg>
        </div>

        <h2 id="blocked-account-title" className="relative text-xl font-black tracking-tight text-white">
          Account Blocked
        </h2>
        <p className="relative mt-1.5 text-sm font-medium text-rose-100/90">
          Access to your account has been restricted
        </p>
      </div>

      {/* Body card */}
      <div className="relative -mt-5 rounded-t-3xl bg-white px-6 pb-6 pt-7">
        <p className="mb-5 text-center text-sm leading-relaxed text-slate-600">
          Your account has been blocked. For account recovery, complete your payment by contacting the admin.
        </p>

        <div className="mb-6 space-y-3">
          <div className="flex items-start gap-3 rounded-2xl border border-amber-100/80 bg-gradient-to-r from-amber-50 via-orange-50 to-amber-50 p-3.5">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 shadow-sm shadow-orange-200/60">
              <svg className="h-5 w-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            </div>
            <div className="min-w-0 text-left">
              <p className="text-xs font-bold text-amber-900">Complete your payment</p>
              <p className="mt-0.5 text-[11px] leading-snug text-amber-800/75">
                Clear any pending dues to restore account access
              </p>
            </div>
          </div>

          <div className="flex items-start gap-3 rounded-2xl border border-violet-100/80 bg-gradient-to-r from-violet-50 via-indigo-50 to-violet-50 p-3.5">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-violet-500 to-indigo-600 shadow-sm shadow-violet-200/60">
              <svg className="h-5 w-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
            </div>
            <div className="min-w-0 text-left">
              <p className="text-xs font-bold text-violet-900">Contact admin support</p>
              <p className="mt-0.5 text-[11px] leading-snug text-violet-800/75">
                Reach out to the admin team for account recovery help
              </p>
            </div>
          </div>
        </div>

        <button
          type="button"
          onClick={onClose}
          className="w-full rounded-xl bg-gradient-to-r from-slate-800 via-slate-900 to-slate-800 py-3 text-sm font-semibold text-white shadow-lg shadow-slate-900/25 transition-all hover:from-slate-700 hover:via-slate-800 hover:to-slate-700 hover:shadow-xl active:scale-[0.98]"
        >
          Understood
        </button>
      </div>
    </div>
  </div>
);

const OkBox = ({ msg }) => msg ? (
  <div className="flex items-start gap-2.5 rounded-xl border border-emerald-200/80 bg-gradient-to-r from-emerald-50 to-teal-50 p-3 text-xs text-emerald-800">
    <svg className="mt-0.5 h-4 w-4 shrink-0 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
    <span>{msg}</span>
  </div>
) : null;

const AuthPrimaryBtn = ({ children, className = '', ...props }) => (
  <button
    className={`w-full rounded-xl bg-gradient-to-r from-violet-600 via-indigo-600 to-violet-700 py-3 text-sm font-semibold text-white shadow-lg shadow-violet-500/25 transition-all hover:from-violet-500 hover:via-indigo-500 hover:to-violet-600 hover:shadow-xl active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-50 ${className}`}
    {...props}
  >
    {children}
  </button>
);

const BRAND_FEATURES = [
  {
    icon: IconUsers,
    title: 'Build your network',
    desc: 'Grow a referral community across multiple levels',
    gradient: 'from-violet-500 to-indigo-600',
  },
  {
    icon: IconSpark,
    title: 'Earn every month',
    desc: 'Get rewarded when your network stays active',
    gradient: 'from-amber-400 to-orange-500',
  },
  {
    icon: IconChart,
    title: 'Track everything',
    desc: 'Dashboard, earnings, payments — all in one place',
    gradient: 'from-emerald-400 to-teal-600',
  },
];

function AuthBrandPanel({ mode }) {
  return (
    <div className="relative overflow-hidden bg-gradient-to-br from-violet-600 via-indigo-700 to-slate-900 text-white lg:flex lg:flex-col lg:justify-between">
      <div className="pointer-events-none absolute -right-16 -top-16 h-56 w-56 rounded-full bg-white/10 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-20 -left-10 h-48 w-48 rounded-full bg-violet-400/20 blur-3xl" />

      <div className="relative px-6 py-8 sm:px-8 sm:py-10 lg:px-10 lg:py-12 xl:px-14">
        <div className="mb-6 flex items-center gap-3 lg:mb-10">
          <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-white/15 shadow-lg ring-2 ring-white/20 backdrop-blur-sm sm:h-14 sm:w-14">
            <span className="text-xl font-black text-white sm:text-2xl" style={SERIF}>S</span>
          </div>
          <div>
            <h1 className="text-xl font-black tracking-tight sm:text-2xl lg:text-3xl" style={SERIF}>
              SuccessFlow
            </h1>
            <p className="text-xs font-medium text-violet-200/90 sm:text-sm">
              Community · Referral · Earnings
            </p>
          </div>
        </div>

        <div className="mb-6 lg:mb-0">
          <h2 className="text-lg font-black leading-snug sm:text-xl lg:text-2xl">
            {mode === 'signup' ? 'Start earning with your network' : 'Welcome back'}
          </h2>
          <p className="mt-2 max-w-md text-sm leading-relaxed text-violet-100/85">
            {mode === 'signup'
              ? 'Create your free account and unlock referral rewards, community tools, and monthly earnings.'
              : 'Sign in to access your dashboard, track referrals, and manage your earnings.'}
          </p>
        </div>

        <ul className="mt-6 hidden space-y-3 sm:block lg:mt-10">
          {BRAND_FEATURES.map(({ icon: FeatureIcon, title, desc, gradient }) => (
            <li
              key={title}
              className="flex items-start gap-3 rounded-2xl border border-white/10 bg-white/10 p-3.5 backdrop-blur-sm sm:p-4"
            >
              <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br ${gradient} shadow-md`}>
                <FeatureIcon className="h-5 w-5 text-white" />
              </div>
              <div className="min-w-0">
                <p className="text-sm font-bold text-white">{title}</p>
                <p className="mt-0.5 text-xs leading-snug text-violet-100/80">{desc}</p>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

// ── Verify Email page ────────────────────────────────────────

export function VerifyEmailPage() {
  const [state,   setState]   = useState('loading');
  const [message, setMessage] = useState('');
  const navigate              = useNavigate();

  useEffect(() => {
    const token = new URLSearchParams(window.location.search).get('token');
    if (!token) { setState('error'); setMessage('No token found in the URL.'); return; }
    api.post(API.VERIFY_EMAIL, { token })
      .then(r => { setState('success'); setMessage(r.data.message); })
      .catch(e => { setState('error');   setMessage(e.response?.data?.error || 'Verification failed.'); });
  }, []);

  return (
    <div className="flex min-h-[calc(100dvh-3.5rem)] items-center justify-center bg-gradient-to-br from-slate-50 via-violet-50/30 to-indigo-50/40 px-4 py-10 sm:min-h-[calc(100dvh-4rem)] sm:px-6">
      <div className="w-full max-w-md">
        <div className="overflow-hidden rounded-3xl border border-white/60 bg-white/95 shadow-xl shadow-slate-200/50">
          <div className="bg-gradient-to-r from-emerald-500 via-teal-500 to-emerald-600 px-6 py-8 text-center text-white">
            <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-2xl bg-white/20 ring-2 ring-white/25">
              {state === 'loading' && (
                <svg className="h-7 w-7 animate-spin text-white" fill="none" viewBox="0 0 24 24" aria-hidden>
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
              )}
              {state === 'success' && (
                <svg className="h-7 w-7" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              )}
              {state === 'error' && (
                <svg className="h-7 w-7" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              )}
            </div>
            <h2 className="text-lg font-black" style={SERIF}>Email Verification</h2>
          </div>
          <div className="px-6 py-7 text-center">
            {state === 'loading' && (
              <p className="text-sm text-slate-500">Verifying your email…</p>
            )}
            {state === 'success' && (
              <>
                <h3 className="mb-2 text-base font-black text-slate-900">Email Verified!</h3>
                <p className="mb-6 text-sm text-slate-500">{message}</p>
                <AuthPrimaryBtn type="button" onClick={() => navigate('/login')}>
                  Go to Login →
                </AuthPrimaryBtn>
              </>
            )}
            {state === 'error' && (
              <>
                <h3 className="mb-2 text-base font-black text-slate-900">Verification Failed</h3>
                <p className="mb-6 text-sm text-slate-500">{message}</p>
                <button
                  type="button"
                  onClick={() => navigate('/login')}
                  className="w-full rounded-xl border border-slate-200 bg-slate-50 py-3 text-sm font-semibold text-slate-700 transition-colors hover:bg-slate-100"
                >
                  ← Back to Login
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Forgot Password page ─────────────────────────────────────

export function ForgotPasswordPage() {
  const [email,   setEmail]   = useState('');
  const [success, setSuccess] = useState('');
  const [error,   setError]   = useState('');
  const [loading, setLoading] = useState(false);
  const navigate              = useNavigate();

  const handleSubmit = async () => {
    if (!email.trim()) { setError('Please enter your email.'); return; }
    setLoading(true); setError(''); setSuccess('');
    try {
      const r = await api.post(API.FORGOT_PASSWORD, { email: email.trim() });
      setSuccess(r.data.message);
    } catch (e) {
      setError(e.response?.data?.error || 'Something went wrong.');
    }
    setLoading(false);
  };

  return (
    <div className="flex min-h-[calc(100dvh-3.5rem)] items-center justify-center bg-gradient-to-br from-slate-50 via-violet-50/30 to-indigo-50/40 px-4 py-10 sm:min-h-[calc(100dvh-4rem)] sm:px-6">
      <div className="w-full max-w-md">
        <div className="overflow-hidden rounded-3xl border border-white/60 bg-white/95 shadow-xl shadow-slate-200/50">
          <div className="border-b border-slate-100 bg-gradient-to-r from-slate-50 via-violet-50/50 to-indigo-50/40 px-6 py-6">
            <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-violet-500 to-indigo-600 shadow-md">
              <IconLock className="h-6 w-6 text-white" />
            </div>
            <h2 className="text-lg font-black text-slate-900" style={SERIF}>Forgot your password?</h2>
            <p className="mt-1 text-xs text-slate-500 sm:text-sm">
              Enter the email linked to your account and we&apos;ll send a reset link.
            </p>
          </div>
          <div className="px-6 py-6">
            {success ? (
              <div className="space-y-4">
                <OkBox msg={success} />
                <div className="rounded-xl border border-slate-100 bg-gradient-to-r from-slate-50 to-violet-50/40 p-4 text-xs text-slate-600">
                  Check your inbox. The link is valid for <strong>1 hour</strong>.
                </div>
                <button
                  type="button"
                  onClick={() => navigate('/login')}
                  className="w-full rounded-xl border border-slate-200 bg-slate-50 py-3 text-sm font-semibold text-slate-700 transition-colors hover:bg-slate-100"
                >
                  ← Back to Login
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                <Field
                  icon={IconMail}
                  placeholder="you@example.com"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onEnter={handleSubmit}
                />
                <ErrBox msg={error} />
                <AuthPrimaryBtn type="button" onClick={handleSubmit} disabled={loading}>
                  {loading ? 'Sending…' : 'Send Reset Link →'}
                </AuthPrimaryBtn>
                <button
                  type="button"
                  onClick={() => navigate('/login')}
                  className="w-full pt-1 text-center text-xs font-semibold text-violet-600 transition-colors hover:text-violet-800"
                >
                  ← Back to Login
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Reset Password page ──────────────────────────────────────

export function ResetPasswordPage() {
  const [form,    setForm]    = useState({ new_password: '', confirm_password: '' });
  const [success, setSuccess] = useState('');
  const [error,   setError]   = useState('');
  const [loading, setLoading] = useState(false);
  const [token,   setToken]   = useState('');
  const navigate              = useNavigate();

  useEffect(() => {
    const t = new URLSearchParams(window.location.search).get('token');
    if (!t) setError('Invalid reset link.');
    else setToken(t);
  }, []);

  const setF = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async () => {
    if (!form.new_password)                          { setError('Enter a new password.'); return; }
    if (form.new_password.length < 8)               { setError('Min 8 characters.'); return; }
    if (form.new_password !== form.confirm_password) { setError('Passwords do not match.'); return; }
    setLoading(true); setError(''); setSuccess('');
    try {
      const r = await api.post(API.RESET_PASSWORD, { token, ...form });
      setSuccess(r.data.message);
    } catch (e) {
      setError(e.response?.data?.error || 'Reset failed. Link may have expired.');
    }
    setLoading(false);
  };

  return (
    <div className="flex min-h-[calc(100dvh-3.5rem)] items-center justify-center bg-gradient-to-br from-slate-50 via-violet-50/30 to-indigo-50/40 px-4 py-10 sm:min-h-[calc(100dvh-4rem)] sm:px-6">
      <div className="w-full max-w-md">
        <div className="overflow-hidden rounded-3xl border border-white/60 bg-white/95 shadow-xl shadow-slate-200/50">
          <div className="border-b border-slate-100 bg-gradient-to-r from-slate-50 via-violet-50/50 to-indigo-50/40 px-6 py-6">
            <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-violet-500 to-indigo-600 shadow-md">
              <IconLock className="h-6 w-6 text-white" />
            </div>
            <h2 className="text-lg font-black text-slate-900" style={SERIF}>Set new password</h2>
            <p className="mt-1 text-xs text-slate-500 sm:text-sm">Must be at least 8 characters long.</p>
          </div>
          <div className="px-6 py-6">
            {success ? (
              <div className="space-y-4 text-center">
                <OkBox msg={success} />
                <AuthPrimaryBtn type="button" onClick={() => navigate('/login')}>
                  Log In Now →
                </AuthPrimaryBtn>
              </div>
            ) : (
              <div className="space-y-4">
                <Field
                  icon={IconLock}
                  placeholder="New password (min 8 chars)"
                  type="password"
                  value={form.new_password}
                  onChange={(e) => setF('new_password', e.target.value)}
                  onEnter={handleSubmit}
                />
                <Field
                  icon={IconLock}
                  placeholder="Confirm new password"
                  type="password"
                  value={form.confirm_password}
                  onChange={(e) => setF('confirm_password', e.target.value)}
                  onEnter={handleSubmit}
                />
                {form.new_password && (
                  <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                    {[
                      { label: '8+ chars', ok: form.new_password.length >= 8 },
                      { label: 'Has number', ok: /\d/.test(form.new_password) },
                      { label: 'Has uppercase', ok: /[A-Z]/.test(form.new_password) },
                      { label: 'Passwords match', ok: form.new_password === form.confirm_password && form.confirm_password.length > 0 },
                    ].map(({ label, ok }) => (
                      <div key={label} className={`flex items-center gap-1.5 text-xs ${ok ? 'text-emerald-600' : 'text-slate-400'}`}>
                        <span className={`flex h-4 w-4 items-center justify-center rounded-full text-[10px] ${ok ? 'bg-emerald-100' : 'bg-slate-100'}`}>
                          {ok ? '✓' : '○'}
                        </span>
                        {label}
                      </div>
                    ))}
                  </div>
                )}
                <ErrBox msg={error} />
                <AuthPrimaryBtn type="button" onClick={handleSubmit} disabled={loading || !token}>
                  {loading ? 'Resetting…' : 'Reset Password →'}
                </AuthPrimaryBtn>
                <button
                  type="button"
                  onClick={() => navigate('/forgot-password')}
                  className="w-full pt-1 text-center text-xs font-semibold text-violet-600 transition-colors hover:text-violet-800"
                >
                  Request a new link
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Main Login / Signup page ─────────────────────────────────

function Login() {
  const [mode, setMode] = useState('login');
  const [form, setForm] = useState({ username: '', password: '', email: '', phone: '', referral_code_used: '' });
  const [error,         setError]         = useState('');
  const [fieldErrors,   setFieldErrors]   = useState({});
  const [loading,       setLoading]       = useState(false);
  const [showResend,    setShowResend]    = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendMsg,     setResendMsg]     = useState('');
  const [blockedDialog, setBlockedDialog] = useState(false);

  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const ref = params.get('ref');
    const m = params.get('mode');
    if (ref) {
      setForm((prev) => ({ ...prev, referral_code_used: ref }));
      setMode('signup');
    } else if (m === 'signup') {
      setMode('signup');
    }
  }, [location.search]);

  useEffect(() => {
    if (location.state?.reason !== 'account_blocked') return;
    setBlockedDialog(true);
    navigate(`${location.pathname}${location.search}`, { replace: true, state: {} });
  }, [location.pathname, location.search, location.state?.reason, navigate]);

  const setF = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    setFieldErrors((prev) => {
      if (!prev[k]) return prev;
      const next = { ...prev };
      delete next[k];
      return next;
    });
  };

  const validateForm = () => {
    const next = {};
    if (!form.username.trim()) {
      next.username = 'Username is required.';
    }
    if (!form.password) {
      next.password = 'Password is required.';
    }
    if (mode === 'signup' && !form.email.trim()) {
      next.email = 'Email address is required.';
    }
    if (mode === 'signup' && !form.phone.trim()) {
      next.phone = 'Mobile number is required.';
    } else if (mode === 'signup' && !isValidMobileOptional(form.phone)) {
      next.phone = 'Enter a valid 10-digit mobile number.';
    }
    return next;
  };

  const handleSubmit = async () => {
    setError('');
    setFieldErrors({});
    setShowResend(false);

    const clientErrors = validateForm();
    if (Object.keys(clientErrors).length > 0) {
      setFieldErrors(clientErrors);
      if (Object.keys(clientErrors).length > 1) {
        setError('Please fix the highlighted fields.');
      }
      return;
    }

    setLoading(true);
    try {
      if (mode === 'login') {
        const res = await api.post(API.LOGIN, { username: form.username, password: form.password });
        await login({ access: res.data.access, refresh: res.data.refresh });
        navigate('/dashboard', { replace: true });
      } else {
        const res = await api.post(API.REGISTER, form);
        await login({ access: res.data.access, refresh: res.data.refresh });
        navigate('/dashboard', { replace: true });
      }
    } catch (e) {
      const err = e.response?.data;
      if (err?.code === 'account_blocked' || err?.is_blocked) {
        setBlockedDialog(true);
        setError('');
        setFieldErrors({});
      } else if (err && typeof err === 'object' && !Array.isArray(err)) {
        const { form: formMsg, fields } = parseApiErrors(err);
        setFieldErrors(fields);
        const fallback =
          mode === 'login'
            ? 'Invalid username or password.'
            : 'Something went wrong.';
        setError(formMsg || (Object.keys(fields).length ? '' : fallback));
        if (String(formMsg).toLowerCase().includes('verif')) setShowResend(true);
      } else {
        setError(
          mode === 'login'
            ? 'Invalid username or password.'
            : 'Something went wrong.'
        );
      }
    }
    setLoading(false);
  };

  const handleResend = async () => {
    setResendLoading(true);
    setResendMsg('');
    try {
      // Backend requires JWT — log in first, then resend verification email.
      const loginRes = await api.post(API.LOGIN, {
        username: form.username,
        password: form.password,
      });
      await login({ access: loginRes.data.access, refresh: loginRes.data.refresh });
      const res = await api.post(API.RESEND_VERIFICATION);
      setResendMsg(res.data.message || 'Verification email sent. Check your inbox.');
    } catch (e) {
      const err = e.response?.data;
      const msg =
        (typeof err?.message === 'string' && err.message) ||
        (typeof err?.error === 'string' && err.error) ||
        (err && typeof err === 'object'
          ? Object.values(err).flat().filter((v) => typeof v === 'string').join(' ')
          : '') ||
        'Could not resend. Check username/password and try again.';
      setResendMsg(msg);
    }
    setResendLoading(false);
  };

  return (
    <div className="min-h-[calc(100dvh-3.5rem)] bg-gradient-to-br from-slate-50 via-violet-50/30 to-indigo-50/40 sm:min-h-[calc(100dvh-4rem)]">
      <div className="mx-auto grid w-full max-w-6xl lg:min-h-[calc(100dvh-3.5rem)] lg:grid-cols-2 lg:shadow-2xl lg:shadow-violet-900/10">
        <AuthBrandPanel mode={mode} />

        <div className="flex items-center justify-center px-4 py-8 sm:px-6 sm:py-10 lg:px-8 lg:py-12 xl:px-12">
          <div className="w-full max-w-md">
            <div className="overflow-hidden rounded-3xl border border-white/60 bg-white/95 shadow-xl shadow-slate-200/50 backdrop-blur-sm">
              {/* Card header */}
              <div className="border-b border-slate-100 bg-gradient-to-r from-slate-50 via-violet-50/50 to-indigo-50/40 px-5 py-5 sm:px-7 sm:py-6">
                <h2 className="text-lg font-black text-slate-900 sm:text-xl" style={SERIF}>
                  {mode === 'login' ? 'Log in to your account' : 'Create your account'}
                </h2>
                <p className="mt-1 text-xs text-slate-500 sm:text-sm">
                  {mode === 'login'
                    ? 'Enter your credentials to continue'
                    : 'Join SuccessFlow and start building your network'}
                </p>

                {/* Tabs */}
                <div className="mt-4 flex rounded-xl bg-slate-100/80 p-1">
                  {['login', 'signup'].map((m) => (
                    <button
                      key={m}
                      type="button"
                      onClick={() => { setMode(m); setError(''); setFieldErrors({}); }}
                      className={`flex flex-1 items-center justify-center gap-1.5 rounded-lg py-2.5 text-xs font-bold transition-all sm:text-sm ${
                        mode === m
                          ? 'bg-gradient-to-r from-violet-600 to-indigo-600 text-white shadow-md shadow-violet-500/25'
                          : 'text-slate-500 hover:text-slate-700'
                      }`}
                    >
                      {m === 'login' ? (
                        <>
                          <IconLock className="h-3.5 w-3.5" />
                          Log In
                        </>
                      ) : (
                        <>
                          <IconUser className="h-3.5 w-3.5" />
                          Sign Up
                        </>
                      )}
                    </button>
                  ))}
                </div>
              </div>

              <div className="px-5 py-5 sm:px-7 sm:py-6">
                {error && (
                  <div className="mb-4 space-y-2">
                    <ErrBox msg={error} />
                    {showResend && (
                      <button
                        type="button"
                        onClick={handleResend}
                        disabled={resendLoading}
                        className="w-full text-xs font-semibold text-violet-600 transition-colors hover:text-violet-800 disabled:opacity-50"
                      >
                        {resendLoading ? 'Sending…' : 'Resend verification email'}
                      </button>
                    )}
                    {resendMsg && <OkBox msg={resendMsg} />}
                  </div>
                )}

                <div className="mb-5 space-y-3.5">
                  <Field
                    label="Username"
                    required
                    icon={IconUser}
                    placeholder="Choose a username"
                    value={form.username}
                    error={fieldErrors.username}
                    onChange={(e) => setF('username', e.target.value)}
                    onEnter={handleSubmit}
                    autoComplete="username"
                  />
                  {mode === 'signup' && (
                    <Field
                      label="Email address"
                      required
                      icon={IconMail}
                      type="email"
                      placeholder="you@example.com"
                      value={form.email}
                      error={fieldErrors.email}
                      onChange={(e) => setF('email', e.target.value)}
                      onEnter={handleSubmit}
                      autoComplete="email"
                    />
                  )}
                  <Field
                    label="Password"
                    required
                    icon={IconLock}
                    type="password"
                    placeholder={mode === 'signup' ? 'Create a password' : 'Your password'}
                    value={form.password}
                    error={fieldErrors.password}
                    onChange={(e) => setF('password', e.target.value)}
                    onEnter={handleSubmit}
                    autoComplete={mode === 'signup' ? 'new-password' : 'current-password'}
                  />
                  {mode === 'signup' && (
                    <Field
                      label="Mobile number"
                      required
                      icon={IconPhone}
                      placeholder="10-digit mobile number"
                      value={form.phone}
                      error={fieldErrors.phone}
                      onChange={(e) => setF('phone', e.target.value)}
                      onEnter={handleSubmit}
                      autoComplete="tel"
                    />
                  )}
                  {mode === 'signup' && (
                    <>
                      <Field
                        label="Referral code"
                        hint="optional"
                        icon={IconGift}
                        placeholder="Friend's referral code"
                        value={form.referral_code_used}
                        error={fieldErrors.referral_code_used}
                        onChange={(e) => setF('referral_code_used', e.target.value)}
                        onEnter={handleSubmit}
                      />
                      <p className="-mt-1 text-[11px] leading-relaxed text-slate-400">
                        Have a friend&apos;s code? Enter it here to join their network.
                      </p>
                    </>
                  )}
                </div>

                {mode === 'login' && (
                  <div className="-mt-2 mb-4 text-right">
                    <button
                      type="button"
                      onClick={() => navigate('/forgot-password')}
                      className="text-xs font-semibold text-violet-600 transition-colors hover:text-violet-800"
                    >
                      Forgot password?
                    </button>
                  </div>
                )}

                <AuthPrimaryBtn type="button" onClick={handleSubmit} disabled={loading}>
                  {loading ? 'Please wait…' : mode === 'login' ? 'Log In →' : 'Create Account →'}
                </AuthPrimaryBtn>
              </div>
            </div>

            <p className="mt-4 text-center text-[11px] text-slate-400 sm:text-xs">
              {mode === 'login' ? (
                <>
                  New here?{' '}
                  <button
                    type="button"
                    onClick={() => { setMode('signup'); setError(''); setFieldErrors({}); }}
                    className="font-semibold text-violet-600 hover:text-violet-800"
                  >
                    Create an account
                  </button>
                </>
              ) : (
                <>
                  Already have an account?{' '}
                  <button
                    type="button"
                    onClick={() => { setMode('login'); setError(''); setFieldErrors({}); }}
                    className="font-semibold text-violet-600 hover:text-violet-800"
                  >
                    Log in
                  </button>
                </>
              )}
            </p>
          </div>
        </div>
      </div>

      {blockedDialog && <BlockedAccountDialog onClose={() => setBlockedDialog(false)} />}
    </div>
  );
}

export default Login;